/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  CURRENT_LANGUAGE,
  CURRENT_WORKSPACE,
  DEFAULT_LANGUAGE,
  LOGGED_USER_MAP,
  USER_WORKSPACE_PREFERENCES,
} from '../constants';
import { APIEvents } from '../enums/api-events';
import type { AuthenticatorsConfig, UserData } from '../models';
import type { FetchResponse, RequestParams } from '../rest-service';
import type { BaseParams, ClassParams, DelegationParams, MethodParams, ObjectParams } from '../utils';
import { buildRequestParams, getAPI } from '../utils';
import { AuthMessageController } from '../utils/auth-message-controller';
import { sendCustomEvent } from '../utils/send-custom-event';
import { API } from './api';
import { OrganizationAPI } from './organization-api';

export type CheckPermParams = BaseParams &
  ClassParams &
  MethodParams &
  Partial<ObjectParams> &
  Partial<DelegationParams>;
export type CheckPermBathParams = BaseParams &
  ClassParams &
  MethodParams &
  Partial<ObjectParams> &
  Partial<DelegationParams>;

export type LoginParams = BaseParams & {
  login?: string;
  password?: string;
  organization?: string;
  authenticatorIdentifier?: string;
  authClassName?: string;
  totpToken?: string;
  params?: { [key: string]: any };
  operationCode?: string;
  operationToken?: string;
};

/**
 * Users API.
 * @author larissa.conde.
 * @author hugo.pimenta
 */
export class UserAPI extends API {
  /**
   * Gets the current application.
   * @returns Current application.
   */
  getCurrentAplication(): string {
    return this.restService.config.app;
  }

  // TODO: Validar a necessidade desse parametro  loggedWithCustomURL
  private setUser(userData: UserData, loggedWithCustomURL = false) {
    const loggedMap = JSON.parse(localStorage.getItem(LOGGED_USER_MAP) ?? '{}');
    const currentApplication = this.getCurrentAplication();
    const logginData: AplicationLoginData = loggedMap[currentApplication] || new AplicationLoginData();
    logginData.orgPrivateId = userData.accessToken?.payload._orgId;
    userData.loggedWithCustomURL = loggedWithCustomURL;
    logginData.users.unshift(userData);
    loggedMap[currentApplication] = logginData;
    localStorage.setItem(LOGGED_USER_MAP, JSON.stringify(loggedMap));
  }

  /**
   * Navigates to authenticator's URL
   * @param loginUrl - Destination URL
   * @param searchParams - Array of searchParams to be appended
   */
  public redirectLoginToAuthenticator(loginUrl: string, searchParams?: { key: string; value: string }[]): void {
    if (loginUrl) {
      const redirectURL = new URL(loginUrl);

      redirectURL.searchParams.set('one_redirect', window.location.href);
      if (searchParams) {
        searchParams.forEach(({ key, value }) => redirectURL.searchParams.set(key, value));
      }

      window.location.href = redirectURL.href;
    }
  }

  /**
   * Performs user login.
   * @param args - Login params.
   * @returns Logged user data.
   */
  async login(args: LoginParams): Promise<FetchResponse<UserData>> {
    const headers = {
      ...(args.organization ? { 'X-Explorer-Account-Token': args.organization } : {}),
      ...(args.authClassName ? { 'X-Explorer-Authentication-Class': args.authClassName } : {}),
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
    };

    let url = `sys/auth/signIn`;
    let method;
    let body;
    let isPublic = false;

    if (args.authenticatorIdentifier) {
      url += `/${args.authenticatorIdentifier}`;
      method = 'POST';
      body = {
        userLogin: args.login,
        userPassword: args.password,
      };
      isPublic = true;
    } else {
      headers['Authorization'] = `Basic ${btoa(`${args.login}:${args.password}`)}`;
      method = 'GET';
    }

    const requestParams: RequestParams = {
      renewToken: false,
      addUserTokenOnHeader: false,
      public: isPublic,
      addBaseUrl: true,
      ...args.requestParams,
      headers,
      url,
      method,
      body,
    };
    const response = await this.restService.fetch(requestParams);
    if (response.data?.accessToken) {
      this.setUser(response.data, args.authClassName != null);
      AuthMessageController.send(APIEvents.LOGIN);
    }
    return response;
  }

  /**
   * Performs a second authentication login.
   * @param args - Login parameters.
   * @param args.operationToken - Operation token for the authentication.
   * @param args.operationCode - Operation code for the authentication.
   * @param args.totpToken - TOTP token for two-factor authentication.
   * @param [args.organization] - Organization identifier.
   * @param [args.authClassName] - Authentication class name.
   * @param [args.requestParams] - Additional request parameters.
   * @returns A Promise that resolves with the response of the authentication.
   */
  async secondAuthLogin(args: LoginParams): Promise<FetchResponse<UserData>> {
    const headers = {
      ...(args.organization ? { 'X-Explorer-Account-Token': args.organization } : {}),
      ...(args.authClassName ? { 'X-Explorer-Authentication-Class': args.authClassName } : {}),
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
    };

    const params = args.params ?? {};

    if (args.totpToken) {
      params['totpToken'] = args.totpToken;
    }

    const url = `sys/auth/mfa/step`;
    const method = 'POST';
    const body = {
      operationToken: args.operationToken,
      operationCode: args.operationCode,
      params,
    };
    const isPublic = true;

    const requestParams: RequestParams = {
      renewToken: false,
      addUserTokenOnHeader: false,
      public: isPublic,
      addBaseUrl: true,
      ...args.requestParams,
      headers,
      url,
      method,
      body,
    };

    const response = await this.restService.fetch(requestParams);
    if (response.data?.accessToken) {
      this.setUser(response.data, args.authClassName != null);
      AuthMessageController.send(APIEvents.LOGIN);
    }
    return response;
  }

  /**
   * Sends an e-mail for password recovery.
   * @param args - User data.
   * @param args.organization - User's organization.
   * @param args.login - User's login.
   * @param args.requestParams - Request params.
   * @returns Response of the request.
   */
  sendPasswordRecoveryEmail(args: {
    organization: string;
    login: string;
    requestParams?: Partial<RequestParams>;
  }): Promise<FetchResponse<string>> {
    const requestParams = {
      ...args.requestParams,
      url: `passwordRecovery/sendRecoveryEmail/${args.organization}/${args.login}`,
    };
    return this.restService.get(requestParams);
  }

  /**
   * Changes the user's password.
   * @param args - Data for password change.
   * @param args.token - User's token.
   * @param args.password - New user password.
   * @param args.requestParams - Request parameters.
   * @returns Response of the request.
   */
  changePassword(args: {
    token: string;
    password: string;
    requestParams?: Partial<RequestParams>;
  }): Promise<FetchResponse<any>> {
    const data = {
      token: args.token,
      password: btoa(args.password),
    };

    const requestParams = {
      ...args.requestParams,
      url: 'passwordRecovery/changePassword',
      body: data,
    };
    return this.restService.post(requestParams);
  }

  /**
   * Renews the authentication of the logged user.
   * @param args - Data for login renewal.
   * @param args.requestParams - Request parameters.
   * @returns Response of the request.
   */
  async refreshLogin(args?: { requestParams?: Partial<RequestParams> }): Promise<FetchResponse<any>> {
    return this.renewToken({ token: this.getUser()?.accessToken.token, ...args });
  }

  /**
   * Renews the authentication token of a specific user.
   * @param args - Data for login and user token renewal.
   * @param args.token - User's token.
   * @param args.requestParams - Request parameters.
   * @returns Response of the request.
   */
  async renewToken(args?: {
    token: string | undefined;
    requestParams?: Partial<RequestParams>;
  }): Promise<FetchResponse<any>> {
    const { token } = args ?? {};
    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
    };

    const url = 'sys/auth/renewToken';

    const requestParams: RequestParams = {
      renewToken: false,
      addUserTokenOnHeader: false,
      ...(args && args.requestParams ? args.requestParams : {}),
      headers,
      url,
    };

    const response = await this.restService.get(requestParams);
    if (response.data) {
      this.setUser(response.data);
    }
    return response;
  }

  /**
   * Gets the time for the user session to expire.
   * @returns Moment when the session will expire.
   */
  getSessionTimeout(): number | undefined | null {
    const accessToken = this.getUser()?.accessToken;
    return accessToken?.payload?.sessionTimeout;
  }

  /**
   * Gets time remaining until the user session expires.
   * @returns Time remaining until the user session expires, in seconds.
   */
  remainingSessionTimeout(): number {
    const sessionTimeout = this.getSessionTimeout();
    if (!sessionTimeout) {
      return 365 * 24 * 60 * 60;
    }

    const currentTimeInSeconds = Date.now() / 1000;

    return sessionTimeout - currentTimeInSeconds;
  }

  /**
   * Determines whether the user session expires in less than 10 minutes.
   * @returns Boolean informing whether the session expires in less than 10 minutes.
   */
  isExpiringSessionTimeout(): boolean {
    if (!this.isLoggedIn()) {
      return false;
    }
    const OFFSET = 600; // 10 minutes
    return this.remainingSessionTimeout() < OFFSET;
  }

  /**
   * Validates the authentication token and login the user if it is valid.
   * @param args - Validation args, including the user's token.
   * @param args.accessToken - User's token.
   * @param args.requestParams - Request parameters.
   * @returns Response of the request.
   */
  async validateToken(args: {
    accessToken?: string;
    requestParams?: Partial<RequestParams>;
  }): Promise<FetchResponse<UserData>> {
    const token = args.accessToken || this.getUser()?.accessToken.token;

    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
    };
    const url = 'sys/auth/validateToken';

    const requestParams: RequestParams = {
      renewToken: false,
      addUserTokenOnHeader: false,
      ...args.requestParams,
      headers,
      url,
    };

    const response = await this.restService.get(requestParams);
    if (response.data) {
      this.setUser(response.data);
    }
    return response;
  }

  /**
   * Logout the user on the server.
   * @param args - Logout params.
   * @param args.requestParams - Request params.
   * @returns Response of the request.
   */
  async logoutServer(args?: { requestParams?: Partial<RequestParams> }): Promise<any> {
    if (!this.getUser()) {
      return Promise.resolve(true);
    }

    const headers = {
      Authorization: `Bearer ${this.getUser()?.accessToken.token}`,
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
    };
    const url = 'sys/auth/signOut';

    const requestParams: RequestParams = {
      renewToken: false,
      addUserTokenOnHeader: false,
      ...(args && args.requestParams ? args.requestParams : {}),
      headers,
      url,
    };

    const response = await this.restService.get(requestParams);
    if (response.data) {
      this.setUser(response.data);
    }
    return response;
  }

  /**
   * Logout the user on the client.
   */
  logoutClient(): void {
    const loggedMap = JSON.parse(localStorage.getItem(LOGGED_USER_MAP) ?? '{}');
    const currentApplication = this.getCurrentAplication();
    delete loggedMap[currentApplication];
    localStorage.setItem(LOGGED_USER_MAP, JSON.stringify(loggedMap));

    localStorage.removeItem(CURRENT_WORKSPACE);
    localStorage.removeItem(USER_WORKSPACE_PREFERENCES);
    localStorage.removeItem(CURRENT_LANGUAGE);
  }

  private async logoutAuthenticator(
    authenticator: AuthenticatorsConfig,
    redirectFunction?: (url: string | undefined) => void,
  ): Promise<FetchResponse<any>> {
    const requestParams: RequestParams = {
      addBaseUrl: false,
      url: authenticator.getLogoutUrl,
      addUserTokenOnHeader: true,
    };

    const response = await this.restService.post(requestParams);
    if (redirectFunction) {
      redirectFunction?.(response?.data?.url);
    } else if (response?.data?.url) {
      window.location.href = response.data.url;
    }

    return response;
  }

  /**
   * Logout current user.
   * @param args - Logout params.
   * @param args.requestParams - Request params.
   * @param args.redirectFunction - Function to be executed after logout
   * @returns Response of the request.
   */
  async logout(args?: {
    requestParams?: Partial<RequestParams>;
    redirectFunction?: (url: string | undefined) => void;
  }): Promise<FetchResponse<any>> {
    const authenticator = await this.getAuthenticator();
    if (authenticator?.hasLogout && authenticator?.getLogoutUrl) {
      const { redirectFunction } = args ?? {};
      const response = await this.logoutAuthenticator(authenticator, redirectFunction);
      this.logoutClient();
      return response;
    }

    const response = await this.logoutServer(args);
    this.logoutClient();

    AuthMessageController.send(APIEvents.LOGOUT);

    return response;
  }

  /**
   * Gets the authenticator used by the user to log in.
   * @returns Promise for the authencitators config used, if it exists
   */
  async getAuthenticator(): Promise<AuthenticatorsConfig | undefined> {
    const organizationAPI = getAPI<OrganizationAPI>('organizationAPI') ?? new OrganizationAPI(this.restService);

    const configs = await organizationAPI.getConfigs({ _id: '' });
    const orgConfig = configs?.data;

    const authenticatorsConfig = orgConfig?.authenticatorsConfig;
    if (!authenticatorsConfig) {
      return;
    }

    const user = this.getUser();
    const authenticatorId = user?.accessToken?.payload?.authenticatorId;

    return authenticatorsConfig.find((el) => el.authenticatorId === authenticatorId);
  }

  /**
   * Check if user is logged in.
   * @returns Boolean informing if the user is logged in.
   */
  isLoggedIn(): boolean {
    const user = this.getUser();
    if (user && this.isTokenExpired()) {
      this.logoutClient();
      return false;
    }

    return !!user;
  }

  /**
   * Gets the private ID of the organization.
   * @returns Private ID of the organization.
   */
  getOrganizationPrivateId(): string | undefined {
    const loggedMap = JSON.parse(localStorage.getItem(LOGGED_USER_MAP) ?? '{}');
    const currentApplication = this.getCurrentAplication();
    const logginData: AplicationLoginData = loggedMap[currentApplication] || new AplicationLoginData();
    return logginData.orgPrivateId;
  }
  /**
   * Gets the current user.
   * @returns Current user data.
   */
  getUser(): UserData | undefined {
    const loggedMap = JSON.parse(localStorage.getItem(LOGGED_USER_MAP) ?? '{}');
    const currentApplication = this.getCurrentAplication();
    const logginData: AplicationLoginData = loggedMap[currentApplication] || new AplicationLoginData();
    const user = logginData.users?.[0];
    return user;
  }

  /**
   * Checks if the token  saved in local storage is expired. An expired token cannot be renewed.
   * @returns Boolean informing if the token is expired.
   */
  isTokenExpired(): boolean {
    const user = this.getUser();

    if (!user?.accessToken?.payload) {
      return true;
    }

    // Tokens generated via application will not have an expiration date and should not be considered expired.
    if (!user.accessToken.payload.exp) {
      return false;
    }

    const tokenExpiration = Number(user.accessToken.payload.exp);
    const currentTimeInSeconds = Date.now() / 1000;
    return currentTimeInSeconds > tokenExpiration;
  }

  /**
   * Sets the current workspace.
   * @param wsId - Workspace ID.
   */
  setCurrentWorkspace(wsId: string): void {
    localStorage.setItem(CURRENT_WORKSPACE, wsId);
  }

  /**
   * Gets the current workspace.
   * @returns Current workspace.
   */
  getCurrentWorkspace(): string | null {
    return localStorage.getItem(CURRENT_WORKSPACE);
  }

  /**
   * Set the current language.
   * @param languageId - Language ID.
   */
  setCurrentLanguage(languageId: string): void {
    sessionStorage.setItem(CURRENT_LANGUAGE, languageId);
    localStorage.setItem(CURRENT_LANGUAGE, languageId);
    sendCustomEvent<{ language: string }>(APIEvents.LANGUAGE_DID_CHANGE_EVENT, { language: languageId });
  }

  /**
   * Get the current language.
   * It will be returned, in preferred order: sessionStorage[CURRENT_LANGUAGE] -> localStorage[CURRENT_LANGUAGE] -> navigator[language] -> DEFAULT_LANGUAGE
   * @param defaultLanguage - Default language.
   * @returns Current language.
   */
  getCurrentLanguage(defaultLanguage?: string): string {
    return (
      sessionStorage.getItem(CURRENT_LANGUAGE) ||
      localStorage.getItem(CURRENT_LANGUAGE) ||
      defaultLanguage ||
      (typeof navigator !== 'undefined' && navigator.language?.split('-')[0]) ||
      DEFAULT_LANGUAGE
    );
  }

  /**
   * Get the current language region.
   * It will be returned, in preferred order: sessionStorage[CURRENT_LANGUAGE] -> localStorage[CURRENT_LANGUAGE] -> navigator[language] -> DEFAULT_LANGUAGE
   * @returns Current language region.
   */
  getCurrentLanguageRegion(): string | null {
    const temp =
      sessionStorage.getItem(CURRENT_LANGUAGE) ||
      localStorage.getItem(CURRENT_LANGUAGE) ||
      ((typeof navigator !== 'undefined' && navigator.language) || DEFAULT_LANGUAGE).split('-');

    if (Array.isArray(temp) && temp.length > 1) {
      return temp[1];
    }
    return null;
  }

  /**
   * Select a language from those supported.
   * It also sets the current language, if it is not set or if one is not supported.
   * @param supportedLanguages - Supported languages.
   * @returns Selected language.
   */
  selectLanguage(supportedLanguages: string[]): string {
    const currentLanguage = this.getCurrentLanguage();
    const selectedLanguage = this.selectMatchingLanguage(supportedLanguages, currentLanguage);

    if (currentLanguage !== selectedLanguage) {
      this.setCurrentLanguage(selectedLanguage);
    }

    sessionStorage.setItem(CURRENT_LANGUAGE, selectedLanguage);
    return selectedLanguage;
  }

  /**
   * Select a language from those supported.
   * It also sets the current language, if it is not set or if one is not supported.
   * @param supportedLanguages - Supported languages.
   * @param currentLanguage - Current language.
   * @returns Selected language.
   */
  selectMatchingLanguage(supportedLanguages: string[], currentLanguage: string): string {
    if (supportedLanguages == null || supportedLanguages.length == 0) {
      return currentLanguage || DEFAULT_LANGUAGE;
    }

    if (currentLanguage == null) {
      return supportedLanguages[0];
    }

    // Busca o idioma exato.
    let selectedLanguage = supportedLanguages.find((lang) => lang === currentLanguage);

    // Busca o idioma ignorando a região
    if (selectedLanguage == null) {
      const prefix = currentLanguage.split('-')[0];

      // Idioma genérico.
      selectedLanguage = supportedLanguages.find((lang) => lang === prefix);

      if (selectedLanguage == null) {
        // Outra região com o mesmo idioma.
        selectedLanguage = supportedLanguages.find((lang) => lang.startsWith(prefix));
      }
    }

    // Nenhum idioma encontrado, utilizar o primeiro.
    if (selectedLanguage == null) {
      selectedLanguage = supportedLanguages[0];
    }

    return selectedLanguage;
  }

  /**
   * Checks if the user has permission to execute a method.
   * @param args - Params for permission check.
   * @returns Boolean indicating whether the user has permission.
   */
  checkPerm(args: CheckPermParams): Promise<FetchResponse<boolean>> {
    const body = {
      method: args.methodIdentifier,
    };

    const requestParams = buildRequestParams({ ...args, methodIdentifier: '_checkPerm', body });

    return this.restService.post(requestParams);
  }

  /**
   * Checks if the user has permission to execute a list of methods.
   * @param args - Parameters for permission check.
   * @param args.list - List of methods to be checked.
   * @param args.requestParams - Request parameters.
   * @returns List of booleans indicating whether the user has permission for each method.
   */
  checkPermBatch(args: {
    list: Array<{ _classId: string; method: string }>;
    requestParams?: Partial<RequestParams>;
  }): Promise<FetchResponse<boolean[]>> {
    const requestParams: RequestParams = {
      ...args.requestParams,
      url: '_checkPerm',
      body: args.list,
    };
    return this.restService.post(requestParams);
  }
}

class AplicationLoginData {
  users: UserData[];
  orgPrivateId?: string;
  constructor() {
    this.users = [];
  }
}
